import React from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { Nursery } from "../types/auth";
import SettingsForm from "./SettingsForm";
import { SETTINGS_KEYS } from "./HierarchyManagementConstants";
import type { WizardData } from "./HierarchyManagement";

interface NurseryStepProps {
  modalAction: "create" | "edit" | "assign-nursery";
  wizardData: WizardData;
  setWizardData: React.Dispatch<React.SetStateAction<WizardData>>;
  nurseryMode: "attach" | "create";
  setNurseryMode: React.Dispatch<React.SetStateAction<"attach" | "create">>;
  nurseryOverride: Record<string, boolean>;
  setNurseryOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  availableNurseries: Nursery[];
}

const NurseryStep: React.FC<NurseryStepProps> = ({
  modalAction,
  wizardData,
  setWizardData,
  nurseryMode,
  setNurseryMode,
  nurseryOverride,
  setNurseryOverride,
  availableNurseries,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'nurseryStep' });
  return (
    <>
      {modalAction !== "edit" && (
        <>
          <h3>{t('nurseryAssignment')}</h3>
          <Form.Group className="mb-3">
            <Form.Label>{t('mode')}</Form.Label>
            <Form.Select
              value={nurseryMode}
              onChange={(e) =>
                setNurseryMode(e.target.value as "attach" | "create")
              }
            >
              <option value="attach">{t('attachExisting')}</option>
              <option value="create">{t('createNew')}</option>
            </Form.Select>
          </Form.Group>
          {nurseryMode === "attach" && (
            <Form.Group className="mb-3">
              <Form.Label>{t('selectNursery')}</Form.Label>
              <Form.Select
                value={wizardData.nursery_id || ""}
                onChange={(e) =>
                  setWizardData((d: WizardData) => ({ ...d, nursery_id: e.target.value }))
                }
              >
                <option value="">{t('selectNurseryPlaceholder')}</option>
                {availableNurseries.map((c) => (
                  <option key={c.id} value={c.nursery_id}>
                    {c.name} ({c.nursery_id})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </>
      )}

      {(modalAction === "edit" || nurseryMode === "create") && (
        <>
          {modalAction === "edit" && <h3>{t('editNurseryDetails')}</h3>}
          <Form.Group className="mb-3">
            <Form.Label>{t('nurseryId')}</Form.Label>
            <Form.Control
              type="text"
              value={wizardData.nursery_id || ""}
              disabled={modalAction === "edit"}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, nursery_id: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('nurseryName')}</Form.Label>
            <Form.Control
              type="text"
              value={wizardData.nursery_name || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({
                  ...d,
                  nursery_name: e.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('status')}</Form.Label>
            <Form.Select
              value={wizardData.status || 1}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({
                  ...d,
                  status: Number(e.target.value) as 1 | 2,
                }))
              }
            >
              <option value={1}>{t('active')}</option>
              <option value={2}>{t('inactive')}</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('email')}</Form.Label>
            <Form.Control
              type="email"
              value={wizardData.email || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, email: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('phone')}</Form.Label>
            <Form.Control
              type="text"
              value={wizardData.phone || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, phone: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('description')}</Form.Label>
            <Form.Control
              as="textarea"
              value={wizardData.description || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, description: e.target.value }))
              }
            />
          </Form.Group>
        </>
      )}
      <div className={`mt-2 ${modalAction != "edit" ? "border-top pt-3" : ""}`}>
        <h6 className="fw-bold">{t('nurserySettingsOverrides')}</h6>
        <SettingsForm
          settings={SETTINGS_KEYS}
          wizardData={wizardData}
          setWizardData={setWizardData}
          override={nurseryOverride}
          setOverride={setNurseryOverride}
          prefix="nursery"
          parentData={{
            timezone: wizardData.location_timezone,
            address: wizardData.location_address,
            repo_th: wizardData.location_repo_th,
            similarity_addition: wizardData.location_similarity_addition,
            valid_th_from: wizardData.location_valid_th_from,
            valid_th_to: wizardData.location_valid_th_to,
          }}
        />
      </div>
    </>
  );
};

export default NurseryStep;
